import { CHAPTER_CARD } from '@/render/palette';
import { BEATS } from '@/story/beats';
import { clearProgress, readProgress } from './save';
import { readSettings, writeSettings } from './settings';

/**
 * The shell: splash, content notice, title, credits.
 *
 * D-102's session. This is the game's first screen that is not a room, and the
 * first thing a player who has never opened it before actually sees.
 *
 * ── When it runs, and why that rule is the whole design ─────────────────────
 *
 * **The shell runs when the URL names no beat.** `?beat=` absent means a person
 * opened the game; `?beat=N` present means something asked for a specific beat —
 * a spec, the chain hand-over, the hidden jump, or Continue. That one rule is
 * what lets 243 existing tests keep booting straight into the world: the harness
 * sets `beat` explicitly on **every** boot (see `tests/e2e/harness.ts`, which
 * says so and gives the reason), so no spec ever meets this code unless it asks
 * to.
 *
 * ── Why it ends in a navigation ─────────────────────────────────────────────
 *
 * Because a beat is entered exactly one way. `main.ts`'s hand-over is
 * `window.location.replace('?beat=N&chained=1')` and argues the case at length:
 * beats do not share a world, and an in-place swap would be a second
 * construction path for a state that today has exactly one — `boot()`. Continue,
 * New Game, the pause menu's restart and the hidden jump are all *the same
 * operation as handing over*, so they are all that navigation (D-126).
 *
 * The cost is one page load and it is already paid: pointer lock and the audio
 * unlock are lost across a navigation exactly as at a cold boot, so the player
 * clicks once. That is true between every pair of beats today.
 *
 * ── The art ─────────────────────────────────────────────────────────────────
 *
 * Chalk on warm near-black, from `CHAPTER_CARD` — the palette the game already
 * uses for the screens that are not rooms. **Rendered from the palette, never
 * restated**, which is the standing rule for every colour in this project.
 *
 * Spec §9 wants these hand-lettered, and they are not yet: that art does not
 * exist, and `pause.ts` already set the precedent of shipping the plain version
 * and saying so rather than blocking on it. Filed in `docs/backlog.md`.
 */

function colour(value: number, alpha = 1): string {
  const red = (value >> 16) & 0xff;
  const green = (value >> 8) & 0xff;
  const blue = value & 0xff;
  return `rgb(${red} ${green} ${blue} / ${alpha})`;
}

const INK = colour(CHAPTER_CARD.ink);
const GROUND = colour(CHAPTER_CARD.ground);

/** The id every shell screen shares, so the harness has one thing to wait on. */
export const SHELL_ROOT_ID = 'rosalie-shell';

/**
 * Seconds the splash holds before the content notice.
 *
 * A tap or a key skips it. It is short because it is a courtesy to the studio
 * name and a cost to everybody who has already read it — including the person
 * testing, who will see it more times today than any player will in a lifetime.
 */
const SPLASH_SECONDS = 1.6;

function screen(): HTMLDivElement {
  const root = document.createElement('div');
  root.id = SHELL_ROOT_ID;
  root.style.cssText = [
    'position:fixed',
    'inset:0',
    'z-index:40',
    'display:flex',
    'flex-direction:column',
    'align-items:center',
    'justify-content:center',
    'gap:1.5rem',
    'padding:calc(env(safe-area-inset-top) + 2rem) calc(env(safe-area-inset-right) + 2rem) calc(env(safe-area-inset-bottom) + 2rem) calc(env(safe-area-inset-left) + 2rem)',
    `background:${GROUND}`,
    `color:${INK}`,
    'font:16px/1.6 system-ui,sans-serif',
    'text-align:center',
    // A shell screen is the only thing on the page. Text selection on a
    // long-press is a phone artefact of a DOM UI and reads as a bug.
    '-webkit-user-select:none',
    'user-select:none',
  ].join(';');
  return root;
}

function button(label: string, primary: boolean): HTMLButtonElement {
  const element = document.createElement('button');
  element.type = 'button';
  element.textContent = label;
  element.style.cssText = [
    'appearance:none',
    'min-width:11rem',
    'padding:0.7rem 1.4rem',
    `border:1px solid ${colour(CHAPTER_CARD.ink, 0.55)}`,
    `background:${primary ? INK : 'transparent'}`,
    `color:${primary ? GROUND : INK}`,
    // Hard corners everywhere in this game's chrome; see `pause.ts`.
    'border-radius:0',
    'box-shadow:none',
    'font:500 0.95rem/1 system-ui,sans-serif',
    'letter-spacing:0.06em',
    'cursor:pointer',
  ].join(';');
  return element;
}

/** Wait for one of several buttons, resolving with the one that was pressed. */
function choose(root: HTMLElement, options: readonly HTMLButtonElement[]): Promise<HTMLButtonElement> {
  return new Promise((resolve) => {
    for (const option of options) {
      option.addEventListener('click', () => {
        resolve(option);
      });
    }
    // Focus the first option so a keyboard player can press Enter without
    // reaching for a pointer, and so the primary action is visibly the default.
    options[0]?.focus();
    root.dataset['ready'] = '1';
  });
}

/** The studio card. Resolves on its own, or early on any input. */
async function splash(): Promise<void> {
  const root = screen();
  const mark = document.createElement('p');
  mark.textContent = 'Rosiverse · CuddlyGames';
  mark.style.cssText = 'margin:0;font:500 1rem/1.4 system-ui,sans-serif;letter-spacing:0.32em;text-transform:uppercase;opacity:0.85';
  root.append(mark);
  root.dataset['screen'] = 'splash';
  root.dataset['ready'] = '1';
  document.body.append(root);

  await new Promise<void>((resolve) => {
    let done = false;
    const finish = (): void => {
      if (done) return;
      done = true;
      window.clearTimeout(timer);
      window.removeEventListener('keydown', finish);
      window.removeEventListener('pointerdown', finish);
      resolve();
    };
    const timer = window.setTimeout(finish, SPLASH_SECONDS * 1000);
    window.addEventListener('keydown', finish);
    window.addEventListener('pointerdown', finish);
  });

  root.remove();
}

/**
 * The content notice, and the one place Gentle Mode is offered before play.
 *
 * Spec §2 requires the notice — *"This game depicts industrial animal
 * farming."* — and D-123 puts the toggle here: the one moment it is in context,
 * and the last moment before beat 1's taking, which is the first thing it
 * changes.
 *
 * The notice appears on every cold open. The *question* does not: once it has
 * been acknowledged the toggle is still there, but pre-answered, so a returning
 * player reads a notice rather than being interrogated.
 */
async function contentNotice(): Promise<void> {
  const root = screen();
  root.dataset['screen'] = 'notice';

  const notice = document.createElement('p');
  notice.textContent = 'This game depicts industrial animal farming.';
  notice.style.cssText = 'margin:0;max-width:26rem;font-size:1.05rem';
  root.append(notice);

  const settings = readSettings();

  const gentle = document.createElement('label');
  gentle.style.cssText = 'display:flex;align-items:center;gap:0.6rem;cursor:pointer;font-size:0.92rem;opacity:0.85';
  const checkbox = document.createElement('input');
  checkbox.type = 'checkbox';
  checkbox.checked = settings.gentle;
  checkbox.setAttribute('data-shell', 'gentle');
  checkbox.style.cssText = 'width:1.1rem;height:1.1rem;accent-color:' + INK;
  const gentleText = document.createElement('span');
  // Spec §7 forbids a narrator, not a settings label. This describes a control.
  gentleText.textContent = 'Gentle Mode — longer cutaways, quieter machinery';
  gentle.append(checkbox, gentleText);
  root.append(gentle);

  const go = button('Continue', true);
  root.append(go);
  document.body.append(root);

  await choose(root, [go]);
  writeSettings({ gentle: checkbox.checked, noticeAcknowledged: true });
  root.remove();
}

/** The credits, shown from the title and at the end of the act. */
async function credits(): Promise<void> {
  const root = screen();
  root.dataset['screen'] = 'credits';

  const heading = document.createElement('p');
  heading.textContent = 'ROSALIE';
  heading.style.cssText = 'margin:0;font:500 1.5rem/1.2 system-ui,sans-serif;letter-spacing:0.3em';
  const studio = document.createElement('p');
  studio.textContent = 'CuddlyGames · Rosiverse';
  studio.style.cssText = 'margin:0;font-size:0.95rem;letter-spacing:0.14em;opacity:0.8';
  const built = document.createElement('p');
  // The build stamp belongs on a screen a phone can read, for the same reason
  // it is in `document.title` rather than the console: devtools is not really
  // an option on the device this game is reviewed on.
  built.textContent = __BUILD_ID__;
  built.style.cssText = 'margin:0;font-size:0.75rem;letter-spacing:0.1em;opacity:0.45';

  const back = button('Back', false);
  root.append(heading, studio, built, back);
  document.body.append(root);

  await choose(root, [back]);
  root.remove();
}

/**
 * The title screen. Resolves with the beat to boot, or loops back into itself
 * after the credits.
 *
 * Spec §9: *"Title screen with continue/new game; credits."* Continue is first
 * and focused when there is a save, because the player who has one is the player
 * who is coming back.
 */
async function title(): Promise<{ beat: number; part: number }> {
  for (;;) {
    const progress = readProgress();
    const root = screen();
    root.dataset['screen'] = 'title';
    if (progress) root.dataset['resume'] = String(progress.beat);

    const heading = document.createElement('h1');
    heading.textContent = 'ROSALIE';
    heading.style.cssText = 'margin:0;font:500 2rem/1.1 system-ui,sans-serif;letter-spacing:0.34em';
    root.append(heading);

    const actions = document.createElement('div');
    actions.style.cssText = 'display:flex;flex-direction:column;gap:0.75rem;align-items:center';

    const options: HTMLButtonElement[] = [];
    if (progress) {
      const entry = BEATS[progress.beat];
      // The beat's own title, from the one table that holds it. A second list of
      // beat names here is the two-derivations failure this project keeps having.
      const resume = button(entry ? `Continue — ${entry.title}` : 'Continue', true);
      resume.dataset['shell'] = 'continue';
      options.push(resume);
    }
    const fresh = button(progress ? 'New Game' : 'Begin', !progress);
    fresh.dataset['shell'] = 'new';
    options.push(fresh);

    const seeCredits = button('Credits', false);
    seeCredits.dataset['shell'] = 'credits';
    options.push(seeCredits);

    actions.append(...options);
    root.append(actions);
    document.body.append(root);

    const pressed = await choose(root, options);
    const action = pressed.dataset['shell'];
    root.remove();

    if (action === 'credits') {
      await credits();
      continue;
    }
    if (action === 'continue' && progress) return { beat: progress.beat, part: progress.part };

    // New Game. **The save is discarded here, not at the first checkpoint.**
    // A player who picks New Game and closes the tab before finishing beat 1
    // has started a new game; finding their old one waiting would be the save
    // silently refusing an instruction they gave it.
    if (progress) {
      const confirmed = await confirmDiscard();
      if (!confirmed) continue;
    }
    clearProgress();
    return { beat: 1, part: 1 };
  }
}

/** A second press before a save is thrown away. Never asked when there is none. */
async function confirmDiscard(): Promise<boolean> {
  const root = screen();
  root.dataset['screen'] = 'confirm';
  const question = document.createElement('p');
  question.textContent = 'Start again from the beginning? Your place will be lost.';
  question.style.cssText = 'margin:0;max-width:24rem';
  const yes = button('Start again', true);
  const no = button('Back', false);
  const actions = document.createElement('div');
  actions.style.cssText = 'display:flex;flex-wrap:wrap;gap:0.75rem;justify-content:center';
  actions.append(yes, no);
  root.append(question, actions);
  document.body.append(root);

  const pressed = await choose(root, [yes, no]);
  root.remove();
  return pressed === yes;
}

/**
 * Run the shell and navigate into the game. **Does not resolve** on the normal
 * path: it replaces the document.
 *
 * `?credits=1` is how the end of the act arrives here — beat 7 has no successor
 * to chain to, so it commits its boundary and comes home. The credits play once,
 * on the way in, and then it is an ordinary title screen.
 */
export async function runShell(params: URLSearchParams): Promise<void> {
  await splash();
  await contentNotice();
  if (params.get('credits') === '1') await credits();
  const destination = await title();
  // `replace`, not `assign`, for the reason the hand-over gives: the back button
  // should leave the game rather than walk the player back into the title and
  // then immediately forwards again.
  // Existing one-room entries keep their short URL. `part=1` is meaningful only
  // once a saved multipart beat needs a room beyond its first.
  const part = destination.part === 1 ? '' : `&part=${destination.part}`;
  window.location.replace(`${window.location.pathname}?beat=${destination.beat}${part}`);
  // The navigation is asynchronous. Returning here would let `boot()` carry on
  // and build a world for a document that is on its way out; a never-resolving
  // promise is the honest description of what happens next.
  await new Promise<never>(() => {});
}
